import { Button, Tab, Tabs } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import HistoryIcon from "@mui/icons-material/History";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { DialogRef, GenericDialog } from "../modals/GenericDialog";
import CreateExport, {
  apiMessage,
  buildInput,
  DEFAULT_ERROR,
  inUserTz,
  YMD,
} from "./CreateExport";
import ExportHistory from "./ExportHistory";
import { AnalyticExport } from "../../shared/types/analyticExport.type";
import { useAuth } from "../../hooks/useAuth";
import { AnalyticTable } from "../../api/analyticTable";
import { CreateExportDto } from "../../dto/CreateExportDto";
import { TAnalyticsFilters } from "../../shared/types/analyticsFilters.type";

type ExcelExportsProps = {
  viewName?: string;
  viewId?: string;
  previousExports?: AnalyticExport[];
  onRefresh?: () => void; // refetch analytics_filters
  isFteching?: boolean;
  initialDateStart?: string | null;
  initialDateEnd?: string | null;
  filters?: Record<string, any>;
  initialFilters?: TAnalyticsFilters; // Add this line to include initialFilters in the props
};

export const useRetryExport = ({
  viewId,
  onCreated,
}: {
  viewId?: string;
  onCreated?: () => void;
}) => {
  const { auth } = useAuth();
  const { t } = useTranslation();
  const mutation = useMutation(
    (item: AnalyticExport) =>
      AnalyticTable.createExportAnalytics({
        user: `${auth?.["@id"]}`,
        layout_name: item.layout_name,
        analytics_view: viewId ? `${viewId}` : undefined,
        input: buildInput(
          item.data_range_start && inUserTz(item.data_range_start).format(YMD),
          item.data_range_end && inUserTz(item.data_range_end).format(YMD),
        ),
      } as CreateExportDto),
    { onSuccess: () => onCreated?.() },
  );

  return {
    retry: (item: AnalyticExport) => mutation.mutate(item),
    retryingId: mutation.isLoading ? mutation.variables?.id : undefined,
    error: mutation.isError
      ? (apiMessage(mutation.error) ?? t(DEFAULT_ERROR))
      : null,
  };
};

function ExcelExports({
  viewName,
  viewId,
  previousExports,
  onRefresh,
  isFteching,
  initialDateStart,
  initialDateEnd,
  filters,
  initialFilters,
}: ExcelExportsProps) {
  const subDialogRef = useRef<DialogRef>(null);
  const [tab, setTab] = useState(0);
  const { t } = useTranslation();

  const { retry, retryingId, error } = useRetryExport({
    viewId,
    onCreated: onRefresh,
  });

  return (
    <>
      <Button variant="contained" onClick={() => subDialogRef.current?.open()}>
        {t("eap:table:buttons:excel")}
      </Button>

      <GenericDialog
        width="lg"
        title={t("eap:export:dialogTitle")}
        ref={subDialogRef}
        hideActions
      >
        <Tabs
          variant="fullWidth"
          value={tab}
          onChange={(_, value: number) => setTab(value)}
          sx={{
            backgroundColor: "transparent",
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
          }}
        >
          <Tab
            icon={<AddCircleOutlineIcon />}
            iconPosition="start"
            label={t("eap:table:buttons:generateExport")}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label={t("navbar:history")}
          />
        </Tabs>

        {tab === 0 && (
          <CreateExport
            previousExports={previousExports}
            viewName={viewName}
            viewId={viewId}
            initialDateStart={initialDateStart}
            initialDateEnd={initialDateEnd}
            filters={filters}
            onCancel={() => subDialogRef.current?.close()}
            isFetching={isFteching}
            onCreated={() => {
              onRefresh?.();
              setTab(1);
            }}
          />
        )}
        {tab === 1 && (
          <ExportHistory
            exports={previousExports}
            isFetching={isFteching}
            onRetry={retry}
            retryingId={retryingId}
            error={error}
            filters={initialFilters}
          />
        )}
      </GenericDialog>
    </>
  );
}

export default ExcelExports;
